import type { SellerConnection } from '../../api/amazonconnect';
import { fetchCommerceHealth } from '../commerceHealth';
import { yesterdayWindows } from '../daily-brief/dayWindows';
import { fetchInventorySample } from '../daily-brief/loadDailyBrief';
import type { FocusInput } from '../daily-brief/focusRules';
import { fetchOrdersMetrics, windowFromMetrics } from '../ordersTruth';
import { buildOpsModel } from './ruleEngine';
import type { OpsIntelligenceModel } from './types';

function staleHours(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / 3_600_000;
}

export async function loadOpsIntelligence(opts: {
  connection: SellerConnection | null;
}): Promise<OpsIntelligenceModel> {
  const conn = opts.connection;
  const connectionId = conn?.id ?? null;

  if (!connectionId || !conn) {
    return buildOpsModel({
      connectionId: null,
      sellerRef: conn?.seller_ref,
      focusInput: null,
      truncated: false,
      error: null,
    });
  }

  const { yesterday, prior } = yesterdayWindows();
  const ordersHref = `/amazon/orders?connection=${encodeURIComponent(connectionId)}&from=${yesterday.isoFrom}&to=${yesterday.isoTo}`;
  const inventoryHref = `/amazon/inventory?connection=${encodeURIComponent(connectionId)}`;
  const connectHref = `/amazon/connect?connection=${encodeURIComponent(connectionId)}`;

  const health = await fetchCommerceHealth(connectionId);

  const ordersRow = health?.domains?.find(
    (d) => d.marketsync_domain === 'orders' || d.key === 'orders',
  );
  const lastSync = ordersRow?.last_sync_at || conn.last_sync_at || null;

  let ordersError: string | undefined;
  let inv = { rows: [] as Awaited<ReturnType<typeof fetchInventorySample>>['rows'], total: null as number | null, zero: 0 };
  let yAgg = windowFromMetrics({
    connection_id: connectionId,
    from: yesterday.isoFrom,
    to: yesterday.isoTo,
    timezone: 'Asia/Kolkata',
    sales: 0,
    order_items: 0,
    units: 0,
    complete: true,
  });
  let pAgg = { ...yAgg };
  let moneySource: string | undefined;

  try {
    const [yMet, pMet, i] = await Promise.all([
      fetchOrdersMetrics(connectionId, { from: yesterday.isoFrom, to: yesterday.isoTo }),
      fetchOrdersMetrics(connectionId, { from: prior.isoFrom, to: prior.isoTo }),
      fetchInventorySample(connectionId).catch(() => ({
        rows: [],
        total: null,
        zero: 0,
      })),
    ]);
    yAgg = windowFromMetrics(yMet);
    pAgg = windowFromMetrics(pMet);
    inv = i;
    moneySource = yMet.source;
  } catch (e) {
    ordersError = e instanceof Error ? e.message : 'Failed to load orders';
  }

  const focusInput: FocusInput = {
    connectionId,
    connectionStatus: conn.status,
    yesterday: yAgg,
    prior: pAgg,
    inventoryZero: inv.rows.length ? inv.zero : null,
    inventorySample: inv.rows.length,
    inventoryTotal: inv.total,
    ordersDomainQuality: ordersRow?.quality,
    ordersLastSync: lastSync ?? undefined,
    ordersError,
    syncStaleHours: staleHours(lastSync),
    ordersScanTruncated: false,
    ordersHrefBase: ordersHref,
    inventoryHrefBase: inventoryHref,
    connectHref,
  };

  return buildOpsModel({
    connectionId,
    sellerRef: conn.seller_ref,
    focusInput,
    truncated: false,
    error: ordersError ?? null,
    source: moneySource,
  });
}
